import React, { useState } from "react";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import AdminIcon from "./adminIcons";
import { ADMIN_BUTTON_SX, ADMIN_COLORS, ADMIN_CONTROL_HEIGHT, ADMIN_FONT, ADMIN_TYPE } from "./adminUi";

var KEY_FORMAT = "YYYY-MM-DD";
var LABEL_FORMAT = "DD MMM YYYY";

/** Forward-looking shortcuts: HR mostly plans for upcoming joiners. */
var PRESETS = [
  { id: "today", label: "Today", range: function (t) { return [t, t]; } },
  { id: "next7", label: "Next 7 days", range: function (t) { return [t, t.add(6, "day")]; } },
  { id: "next15", label: "Next 15 days", range: function (t) { return [t, t.add(14, "day")]; } },
  { id: "next30", label: "Next 30 days", range: function (t) { return [t, t.add(29, "day")]; } },
  { id: "restOfMonth", label: "Rest of this month", range: function (t) { return [t, t.endOf("month")]; } },
  { id: "nextMonth", label: "Next month", range: function (t) { var n = t.add(1, "month"); return [n.startOf("month"), n.endOf("month")]; } },
];

function toKey(date) {
  return date ? date.format(KEY_FORMAT) : "";
}

/** Day cell that paints the start/end caps and the in-between band. */
function RangeDay(props) {
  var { day, rangeStart, rangeEnd, hoverKey, onHoverKey, outsideCurrentMonth, ...other } = props;
  var key = toKey(day);
  var end = rangeEnd || (rangeStart && hoverKey && hoverKey > rangeStart ? hoverKey : "");
  var isStart = Boolean(rangeStart) && key === rangeStart;
  var isEnd = Boolean(end) && key === end;
  var inBand = Boolean(rangeStart && end) && key > rangeStart && key < end;
  var isEdge = isStart || isEnd;
  var hasBand = Boolean(rangeStart && end) && (inBand || (isStart && end !== rangeStart) || (isEnd && end !== rangeStart));

  if (outsideCurrentMonth) {
    return <PickersDay {...other} day={day} outsideCurrentMonth />;
  }

  return (
    <Box
      onMouseEnter={function () {
        if (onHoverKey) onHoverKey(key);
      }}
      sx={{
        bgcolor: hasBand ? ADMIN_COLORS.brandSoft : "transparent",
        borderTopLeftRadius: isStart ? "50%" : 0,
        borderBottomLeftRadius: isStart ? "50%" : 0,
        borderTopRightRadius: isEnd ? "50%" : 0,
        borderBottomRightRadius: isEnd ? "50%" : 0,
      }}
    >
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={false}
        selected={isEdge}
        disableMargin
        sx={{
          width: 36,
          height: 36,
          mx: "2px",
          fontFamily: ADMIN_FONT,
          fontSize: 13,
          color: ADMIN_COLORS.label,
          bgcolor: "transparent",
          "&:hover": { bgcolor: isEdge ? ADMIN_COLORS.brandHover : ADMIN_COLORS.tabActive },
          "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus": {
            bgcolor: ADMIN_COLORS.brand,
            color: "#FFFFFF",
          },
          "&.MuiPickersDay-today:not(.Mui-selected)": { borderColor: ADMIN_COLORS.brand },
        }}
      />
    </Box>
  );
}

var YEARS_PER_PAGE = 12;
var MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function GridCell(props) {
  return (
    <ButtonBase
      onClick={props.onClick}
      aria-pressed={props.selected}
      sx={{
        height: 36,
        borderRadius: "6px",
        fontFamily: ADMIN_FONT,
        fontSize: 14,
        fontWeight: props.selected ? 600 : 400,
        color: props.selected ? "#FFFFFF" : ADMIN_COLORS.label,
        bgcolor: props.selected ? ADMIN_COLORS.brand : "transparent",
        border: "1px solid " + (props.current && !props.selected ? ADMIN_COLORS.brand : "transparent"),
        "&:hover": { bgcolor: props.selected ? ADMIN_COLORS.brandHover : ADMIN_COLORS.brandSoft },
        "&.Mui-focusVisible": { outline: "2px solid " + ADMIN_COLORS.brand, outlineOffset: "1px" },
      }}
    >
      {props.children}
    </ButtonBase>
  );
}

/** Jump straight to any month/year: year grid (paged by 12) next to a month grid. */
function MonthYearPanel(props) {
  var [year, setYear] = useState(props.month.year());
  var [pageStart, setPageStart] = useState(props.month.year() - (props.month.year() % YEARS_PER_PAGE));
  var today = dayjs();
  var years = [];
  for (var y = pageStart; y < pageStart + YEARS_PER_PAGE; y++) years.push(y);
  var gridSx = { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "6px" };
  var headSx = { display: "flex", alignItems: "center", justifyContent: "space-between", height: 32, mb: "8px" };
  var titleSx = { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: 600, color: ADMIN_COLORS.label };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: props.wide ? "row" : "column",
        gap: "20px",
        width: props.wide ? 608 : 296,
        minHeight: props.wide ? 300 : undefined,
        py: "4px",
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Box sx={headSx}>
          <IconButton
            size="small"
            aria-label="Previous years"
            onClick={function () {
              setPageStart(pageStart - YEARS_PER_PAGE);
            }}
            sx={{ color: ADMIN_COLORS.label }}
          >
            <AdminIcon name="ChevronRight" size={16} sx={{ transform: "rotate(180deg)" }} />
          </IconButton>
          <Typography sx={titleSx}>
            {pageStart} – {pageStart + YEARS_PER_PAGE - 1}
          </Typography>
          <IconButton
            size="small"
            aria-label="Next years"
            onClick={function () {
              setPageStart(pageStart + YEARS_PER_PAGE);
            }}
            sx={{ color: ADMIN_COLORS.label }}
          >
            <AdminIcon name="ChevronRight" size={16} />
          </IconButton>
        </Box>
        <Box sx={gridSx} role="group" aria-label="Year">
          {years.map(function (item) {
            return (
              <GridCell
                key={item}
                selected={item === year}
                current={item === today.year()}
                onClick={function () {
                  setYear(item);
                }}
              >
                {item}
              </GridCell>
            );
          })}
        </Box>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Box sx={headSx}>
          <Typography sx={Object.assign({ px: "6px" }, titleSx)}>{year}</Typography>
          <Button variant="text" onClick={props.onCancel} sx={{ height: 30, px: "8px", fontSize: 14, color: ADMIN_COLORS.brand, textTransform: "none" }}>
            Back to calendar
          </Button>
        </Box>
        <Box sx={gridSx} role="group" aria-label="Month">
          {MONTH_NAMES.map(function (name, index) {
            return (
              <GridCell
                key={name}
                selected={year === props.month.year() && index === props.month.month()}
                current={year === today.year() && index === today.month()}
                onClick={function () {
                  props.onSelect(dayjs().year(year).month(index).startOf("month"));
                }}
              >
                {name}
              </GridCell>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

function MonthPane(props) {
  return (
    <Box sx={{ width: 296 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 32, px: "4px" }}>
        {props.onPrev ? (
          <IconButton size="small" aria-label="Previous month" onClick={props.onPrev} sx={{ color: ADMIN_COLORS.label }}>
            <AdminIcon name="ChevronRight" size={16} sx={{ transform: "rotate(180deg)" }} />
          </IconButton>
        ) : (
          <Box sx={{ width: 34 }} />
        )}
        <ButtonBase
          onClick={props.onTitleClick}
          aria-label={"Choose month and year, currently " + props.month.format("MMMM YYYY")}
          sx={{
            gap: "4px",
            px: "8px",
            height: 30,
            borderRadius: "6px",
            fontFamily: ADMIN_FONT,
            fontSize: 14,
            fontWeight: 600,
            color: ADMIN_COLORS.label,
            "&:hover": { bgcolor: ADMIN_COLORS.brandSoft, color: ADMIN_COLORS.brand },
            "&.Mui-focusVisible": { outline: "2px solid " + ADMIN_COLORS.brand, outlineOffset: "1px" },
          }}
        >
          {props.month.format("MMMM YYYY")}
          <AdminIcon name="CaretDown" size={14} />
        </ButtonBase>
        {props.onNext ? (
          <IconButton size="small" aria-label="Next month" onClick={props.onNext} sx={{ color: ADMIN_COLORS.label }}>
            <AdminIcon name="ChevronRight" size={16} />
          </IconButton>
        ) : (
          <Box sx={{ width: 34 }} />
        )}
      </Box>
      <DateCalendar
        key={props.month.format("YYYY-MM")}
        value={null}
        defaultCalendarMonth={props.month}
        views={["day"]}
        onChange={props.onPick}
        slots={{ day: RangeDay }}
        slotProps={{ day: props.dayProps }}
        sx={{
          width: 296,
          height: "auto",
          maxHeight: "none",
          m: 0,
          "& .MuiPickersCalendarHeader-root": { display: "none" },
          "& .MuiDayCalendar-header": { justifyContent: "space-between", px: "2px" },
          "& .MuiDayCalendar-weekDayLabel": { width: 40, m: 0, fontFamily: ADMIN_FONT, fontSize: 12, color: ADMIN_COLORS.labelMuted },
          "& .MuiDayCalendar-weekContainer": { justifyContent: "space-between", m: "2px 0" },
          "& .MuiDayCalendar-slideTransition": { minHeight: 252 },
          "& .MuiPickersSlideTransition-root": { minHeight: 252 },
        }}
      />
    </Box>
  );
}

/** Start calendar opens on the start date's month, end calendar on the end date's month (or the next one). */
function panesFor(start, end) {
  var left = (start ? dayjs(start) : dayjs()).startOf("month");
  var right = end ? dayjs(end).startOf("month") : null;
  return [left, right && right.isAfter(left) ? right : left.add(1, "month")];
}

/**
 * DOJ range filter: inclusive { start, end } as YYYY-MM-DD strings ("" = open).
 * Built on the free DateCalendar (no Pro licence).
 */
function DojRangePicker(props) {
  var value = props.value || { start: "", end: "" };
  var twoMonths = useMediaQuery("(min-width:900px)");
  var [anchorEl, setAnchorEl] = useState(null);
  var [draft, setDraft] = useState({ start: "", end: "" });
  var [hoverKey, setHoverKey] = useState("");
  var [months, setMonths] = useState(function () {
    return panesFor("", "");
  });
  var [pickingPane, setPickingPane] = useState(-1);

  function setPaneMonth(pane, next) {
    setMonths(function (prev) {
      var copy = prev.slice();
      copy[pane] = next;
      return copy;
    });
  }

  function open(e) {
    setDraft({ start: value.start || "", end: value.end || "" });
    setHoverKey("");
    setPickingPane(-1);
    setMonths(panesFor(value.start, value.end));
    setAnchorEl(e.currentTarget);
  }

  function close() {
    setAnchorEl(null);
  }

  function pick(date) {
    if (!date) return;
    var key = toKey(date);
    setDraft(function (prev) {
      if (!prev.start || prev.end) return { start: key, end: "" };
      if (key < prev.start) return { start: key, end: prev.start };
      return { start: prev.start, end: key };
    });
  }

  function applyPreset(preset) {
    var range = preset.range(dayjs().startOf("day"));
    setDraft({ start: toKey(range[0]), end: toKey(range[1]) });
    setMonths(panesFor(toKey(range[0]), toKey(range[1])));
    setPickingPane(-1);
  }

  function apply() {
    var next = draft.start ? { start: draft.start, end: draft.end || draft.start } : { start: "", end: "" };
    props.onChange(next);
    close();
  }

  function clearAll() {
    setDraft({ start: "", end: "" });
    props.onChange({ start: "", end: "" });
    close();
  }

  var dayProps = {
    rangeStart: draft.start,
    rangeEnd: draft.end,
    hoverKey: hoverKey,
    onHoverKey: setHoverKey,
  };
  var hasValue = Boolean(value.start);
  var activePreset = PRESETS.find(function (preset) {
    var range = preset.range(dayjs().startOf("day"));
    return draft.start === toKey(range[0]) && draft.end === toKey(range[1]);
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ButtonBase
        onClick={open}
        aria-haspopup="dialog"
        aria-label="Filter by date of joining"
        sx={{
          height: ADMIN_CONTROL_HEIGHT,
          pl: "12px",
          pr: "10px",
          gap: "8px",
          border: "1px solid " + (anchorEl ? ADMIN_COLORS.brand : ADMIN_COLORS.line),
          borderRadius: "6px",
          bgcolor: ADMIN_COLORS.card,
          fontFamily: ADMIN_FONT,
          fontSize: ADMIN_TYPE.button.fontSize,
          lineHeight: ADMIN_TYPE.button.lineHeight,
          color: hasValue ? ADMIN_COLORS.label : ADMIN_COLORS.labelMuted,
          whiteSpace: "nowrap",
          "&:hover": { borderColor: ADMIN_COLORS.brand },
        }}
      >
        <span>{hasValue ? dayjs(value.start).format(LABEL_FORMAT) : "Start date"}</span>
        <AdminIcon name="ArrowRight" size={16} sx={{ color: ADMIN_COLORS.labelMuted }} />
        <span>{hasValue ? dayjs(value.end || value.start).format(LABEL_FORMAT) : "End date"}</span>
        <AdminIcon name="CalendarDots" size={16} sx={{ color: ADMIN_COLORS.label, ml: "4px" }} />
      </ButtonBase>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          role: "dialog",
          "aria-label": "Select date of joining range",
          sx: {
            mt: "6px",
            borderRadius: "12px",
            border: "1px solid " + ADMIN_COLORS.lineSoft,
            boxShadow: "0 12px 32px rgba(23, 26, 28, 0.14)",
            bgcolor: "#FFFFFF",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", sm: "column" },
              flexWrap: "wrap",
              gap: "2px",
              p: "12px",
              width: { sm: 168 },
              borderRight: { sm: "1px solid " + ADMIN_COLORS.lineSoft },
              borderBottom: { xs: "1px solid " + ADMIN_COLORS.lineSoft, sm: "none" },
            }}
          >
            <Typography sx={{ width: "100%", fontFamily: ADMIN_FONT, fontSize: 12, color: ADMIN_COLORS.labelMuted, px: "8px", pb: "6px" }}>
              Quick select
            </Typography>
            {PRESETS.map(function (preset) {
              var active = activePreset && activePreset.id === preset.id;
              return (
                <ButtonBase
                  key={preset.id}
                  onClick={function () {
                    applyPreset(preset);
                  }}
                  sx={{
                    justifyContent: "flex-start",
                    px: "8px",
                    py: "7px",
                    borderRadius: "6px",
                    fontFamily: ADMIN_FONT,
                    fontSize: 14,
                    textAlign: "left",
                    color: active ? ADMIN_COLORS.brand : ADMIN_COLORS.label,
                    bgcolor: active ? ADMIN_COLORS.brandSoft : "transparent",
                    "&:hover": { bgcolor: ADMIN_COLORS.brandSoft },
                  }}
                >
                  {preset.label}
                </ButtonBase>
              );
            })}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              sx={{ display: "flex", gap: "16px", px: "12px", pt: "12px" }}
              onMouseLeave={function () {
                setHoverKey("");
              }}
            >
              {pickingPane !== -1 ? (
                <MonthYearPanel
                  wide={twoMonths}
                  month={months[pickingPane]}
                  onCancel={function () {
                    setPickingPane(-1);
                  }}
                  onSelect={function (chosen) {
                    setPaneMonth(pickingPane, chosen);
                    setPickingPane(-1);
                  }}
                />
              ) : (
                (twoMonths ? [0, 1] : [0]).map(function (pane) {
                  return (
                    <MonthPane
                      key={pane}
                      month={months[pane]}
                      dayProps={dayProps}
                      onPick={pick}
                      onTitleClick={function () {
                        setPickingPane(pane);
                      }}
                      onPrev={function () {
                        setPaneMonth(pane, months[pane].subtract(1, "month"));
                      }}
                      onNext={function () {
                        setPaneMonth(pane, months[pane].add(1, "month"));
                      }}
                    />
                  );
                })
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "10px",
                px: "16px",
                py: "12px",
                borderTop: "1px solid " + ADMIN_COLORS.lineSoft,
              }}
            >
              <Typography sx={{ flex: 1, minWidth: 180, fontFamily: ADMIN_FONT, fontSize: 13, color: ADMIN_COLORS.labelMuted }}>
                {draft.start
                  ? dayjs(draft.start).format(LABEL_FORMAT) +
                    " – " +
                    (draft.end ? dayjs(draft.end).format(LABEL_FORMAT) : "select end date")
                  : "Select a start date"}
              </Typography>
              <Button
                variant="text"
                onClick={clearAll}
                disabled={!draft.start && !hasValue}
                sx={{ height: 32, px: "10px", fontSize: 14, color: ADMIN_COLORS.brand, textTransform: "none" }}
              >
                Clear
              </Button>
              <Button variant="outlined" onClick={close} sx={ADMIN_BUTTON_SX.outlined}>
                Cancel
              </Button>
              <Button variant="contained" onClick={apply} disabled={!draft.start && !hasValue} sx={ADMIN_BUTTON_SX.contained}>
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
}

export default DojRangePicker;
